import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormControl, InputLabel, LinearProgress, MenuItem, Select } from '@mui/material';
import { saveAs } from 'file-saver';
import createManualReadingsWorker from './createManualReadingsWorker';
import useAxiosProtect from '../../../../../../hooks/useAxiosProtect';

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const BATCH_SIZE = 10000;

const ManualReadingsModal = props => {
    const axiosProtect = useAxiosProtect();
    const pollRef = useRef(null);
    const workerRef = useRef(null);
    const pendingRef = useRef(new Map());
    const requestRef = useRef(0);
    const resumeRef = useRef(null);
    const mountedRef = useRef(true);
    useEffect(()=>{
        mountedRef.current=true;
        const pending=pendingRef.current;
        return ()=>{
            mountedRef.current=false;
            clearTimeout(pollRef.current);
            workerRef.current?.terminate();
            workerRef.current=null;
            pending.forEach(({reject})=>reject(new Error('Upload dialog closed')));
            pending.clear();
        };
    },[]);
    const [sensors, setSensors] = useState([]);
    const [timezone, setTimezone] = useState('');
    const [sensorID, setSensorID] = useState('');
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const [summary, setSummary] = useState(null);
    const [validation, setValidation] = useState(null);
    const [rejectedPage,setRejectedPage] = useState(0);
    const [rejectedRows,setRejectedRows] = useState([]);
    const [reportBusy,setReportBusy] = useState(false);

    const selectedSensor = sensors.find(sensor => sensor.id === Number(sensorID));

    useEffect(() => {
        if(!props.show) return;
        setSensorID('');
        setTimezone('');
        setFile(null);
        setError('');
        setLoading(false);
        setProgress(0);
        setStatus('');
        setSummary(null);
        setValidation(null);setRejectedRows([]);setRejectedPage(0);setReportBusy(false);
        resumeRef.current=null;

        axiosProtect.get(`/v2s/v2-sensors/manual-readings/sensors?site_id=${props.siteID}&utility=${props.utility}`)
            .then(response => {
                setSensors(response.data.sensors);
                setTimezone(response.data.timezone);
            })
            .catch(requestError => setError(requestError.response?.data?.error || 'Unable to load sensors'));

        return () => {
            if(pollRef.current) clearTimeout(pollRef.current);
        };
    }, [props.show, props.siteID, props.utility, axiosProtect]);

    const workerRequest = (action,data={}) => new Promise((resolve,reject)=>{
        if(!workerRef.current) {
            const worker=createManualReadingsWorker();
            worker.onmessage=event=>{
                if(!mountedRef.current) return;
                if(event.data.id===undefined){setProgress(event.data.progress);setStatus(event.data.message);return;}
                const pending=pendingRef.current.get(event.data.id);
                if(!pending) return;
                pendingRef.current.delete(event.data.id);
                if(event.data.error) pending.reject(new Error(event.data.error));else pending.resolve(event.data.result);
            };
            worker.onerror=()=>{
                pendingRef.current.forEach(({reject})=>reject(new Error('Unable to validate the workbook. Try again or split it into smaller files.')));
                pendingRef.current.clear();worker.terminate();workerRef.current=null;
                resumeRef.current=null;setValidation(null);setRejectedRows([]);setFile(null);setLoading(false);
            };
            workerRef.current=worker;
        }
        const id=++requestRef.current;
        pendingRef.current.set(id,{resolve,reject});
        workerRef.current.postMessage({id,action,...data},data.buffer?[data.buffer]:[]);
    });
    const retryRequest = async action => {
        for(let attempt=0;;attempt++) {
            try{return await action();}
            catch(error){
                if(attempt>=3 || (error.response && error.response.status<500 && error.response.status!==429)) throw error;
                await new Promise(resolve=>setTimeout(resolve,1000*2**attempt));
            }
        }
    };

    const downloadTemplate = async () => {
        try{
            const response = await axiosProtect.get('/v2s/v2-sensors/manual-readings/template', {responseType:'blob'});
            saveAs(response.data, 'sensor-readings-template.xlsx');
        }catch(requestError){
            setError(requestError.response?.data?.error || 'Unable to download XLSX template');
        }
    };

    const showJob = job => {
        setProgress(job.progress||0);
        setStatus(job.message||job.status);
        if(job.status==='complete') {
            setSummary(job.summary);setLoading(false);
            if(resumeRef.current?.key) sessionStorage.removeItem(resumeRef.current.key);
            return true;
        }
        if(job.status==='failed') {
            if(resumeRef.current?.key) sessionStorage.removeItem(resumeRef.current.key);
            if(resumeRef.current){resumeRef.current.approved=false;delete resumeRef.current.id;delete resumeRef.current.key;}
            throw new Error(job.error||'Upload failed; no readings changed');
        }
        return false;
    };
    const pollJob = jobID => {
        retryRequest(()=>axiosProtect.get(`/v2s/v2-sensors/manual-readings/status?job_id=${jobID}`)).then(response=>{
            if(!mountedRef.current) return;
            if(!showJob(response.data.job)) pollRef.current=setTimeout(()=>pollJob(jobID),2000);
        }).catch(requestError=>{
            if(!mountedRef.current) return;
            setError(requestError.response?.data?.error||requestError.message||'Unable to check progress. Click Retry to reconnect.');
            setLoading(false);
        });
    };
    const uploadReadings = async () => {
        setError('');
        if(sensorID===''){setError('Select a sensor');return;}
        if(!file){setError('Select an XLSX file');return;}
        if(file.size>MAX_FILE_BYTES){setError('XLSX files cannot exceed 15 MB');return;}
        if(!file.name.toLowerCase().endsWith('.xlsx')){setError('Only XLSX files are supported');return;}
        setLoading(true);setProgress(0);setStatus('Reading and validating the complete workbook');
        try {
            let session=resumeRef.current;
            if(!session || session.file!==file || session.sensorID!==sensorID) {
                const buffer=await file.arrayBuffer();
                const validated=await workerRequest('validate',{buffer,timezone});
                session={...validated,file,sensorID,approved:false};resumeRef.current=session;
                setValidation(validated);setRejectedRows(validated.rejectedPreview);setRejectedPage(0);
                setStatus('Review the validation results before uploading');setLoading(false);
                return;
            }
            if(!session.approved){setLoading(false);return;}
            if(!session.id){
                const key=`manual-upload-reviewed:${props.siteID}:${sensorID}:${session.fileHash}`;
                let id=sessionStorage.getItem(key);
                if(!id){id=crypto.randomUUID();sessionStorage.setItem(key,id);}
                session.key=key;session.id=id;
            }
            const response=await retryRequest(()=>axiosProtect.post('/v2s/v2-sensors/manual-readings/uploads',{
                upload_id:session.id,site_id:Number(props.siteID),sensor_id:Number(sensorID),
                file_size:file.size,total_rows:session.totalRows,file_hash:session.fileHash,
                source_rows:session.sourceRows,rejected_rows:session.rejectedRows,skip_rejected_rows:session.rejectedRows>0
            }));
            if(showJob(response.data.job)) return;
            if(response.data.job.status!=='uploading'){pollJob(session.id);return;}
            const existing=await retryRequest(()=>axiosProtect.get(`/v2s/v2-sensors/manual-readings/status?job_id=${session.id}`));
            const accepted=new Set(existing.data.job.batches);
            for(let index=0;index<Math.ceil(session.totalRows/BATCH_SIZE);index++) {
                if(accepted.has(index)) continue;
                const readings=await workerRequest('batch',{index});
                const batch=await retryRequest(()=>axiosProtect.post('/v2s/v2-sensors/manual-readings/uploads/batch',{
                    upload_id:session.id,batch_index:index,readings
                }));
                showJob(batch.data.job);
            }
            const completed=await retryRequest(()=>axiosProtect.post('/v2s/v2-sensors/manual-readings/uploads/complete',{upload_id:session.id}));
            if(!showJob(completed.data.job)) pollJob(session.id);
        }catch(requestError){
            setError(requestError.response?.data?.error||requestError.message||'Unable to upload readings. Click Retry to resume.');
            setLoading(false);
        }
    };

    const confirmAcceptedReadings = () => {
        if(!resumeRef.current || !validation?.totalRows) return;
        resumeRef.current.approved=true;
        uploadReadings();
    };
    const chooseAnotherFile = () => {
        if(loading || resumeRef.current?.approved) return;
        workerRef.current?.terminate();workerRef.current=null;resumeRef.current=null;
        setValidation(null);setRejectedRows([]);setRejectedPage(0);setFile(null);setError('');setProgress(0);setStatus('');
    };
    const downloadRejections = async () => {
        setReportBusy(true);
        try {
            const csv=await workerRequest('rejectionReport');
            saveAs(new Blob([csv],{type:'text/csv;charset=utf-8;'}),'manual-readings-rejected.csv');
        }catch(requestError){setError(requestError.message||'Unable to download rejection report');}
        finally {setReportBusy(false);}
    };
    const changeRejectionPage = async index => {
        setReportBusy(true);
        try {const rows=await workerRequest('rejectedPage',{index});setRejectedRows(rows);setRejectedPage(index);}
        catch(requestError){setError(requestError.message||'Unable to read rejected rows');}
        finally {setReportBusy(false);}
    };

    const closeModal = () => {
        if(loading||reportBusy) return;
        if(pollRef.current) clearTimeout(pollRef.current);
        workerRef.current?.terminate();
        workerRef.current=null;
        resumeRef.current=null;
        props.closeModal();
    };

    return (
        <Modal
            show={props.show}
            onHide={closeModal}
            backdrop="static"
            keyboard={!loading&&!reportBusy}
            centered
            className="opp-modal-root"
            backdropClassName="opp-modal-backdrop"
            dialogClassName="opp-modal-cw"
        >
            <Modal.Header closeButton={!loading&&!reportBusy}>
                <Modal.Title>Add manual readings</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error !== '' && <p style={{color:'red', textAlign:'center'}}>{error}</p>}

                {!summary && !validation && <>
                    <FormControl fullWidth style={{marginBottom:'2vh'}}>
                        <InputLabel id="manual-reading-sensor-label">Sensor</InputLabel>
                        <Select
                            labelId="manual-reading-sensor-label"
                            id="manual-reading-sensor"
                            label="Sensor"
                            value={sensorID}
                            onChange={event => setSensorID(event.target.value)}
                            disabled={loading}
                            MenuProps={{disablePortal:true}}
                        >
                            {sensors.map(sensor => <MenuItem key={sensor.id} value={sensor.id}>
                                {sensor.name} ({sensor.supplier_name}, {sensor.unit_symbol})
                            </MenuItem>)}
                        </Select>
                    </FormControl>

                    <p style={{marginBottom:'0.5rem'}}><strong>XLSX rules</strong></p>
                    <ul style={{paddingLeft:'1.25rem'}}>
                        <li>Use exactly two columns: <code>timestamp,value</code>.</li>
                        <li>Prefer timestamps with the source UTC offset: <code>2025-11-02T01:00:26-04:00</code>, or UTC: <code>2025-11-02T05:00:26Z</code>.</li>
                        <li>Keep offset-bearing timestamps as text in Excel; do not add an offset unless it is known from the source.</li>
                        <li>Without an offset, use <code>YYYY-MM-DD HH:mm:ss</code> in this site's local timezone{timezone ? ` (${timezone})` : ''}. Repeated or nonexistent daylight-saving times are rejected.</li>
                        <li>Values must use the sensor's configured reporting unit{selectedSensor ? ` (${selectedSensor.unit_symbol})` : ''}.</li>
                        <li>Future, invalid, and more-than-three-year-old timestamps are rejected.</li>
                        <li>Duplicate instants are rejected, including equivalent timestamps written with different offsets.</li>
                        <li>Use stored values; formulas are not accepted.</li>
                        <li>Existing readings at the same sensor and second are overwritten.</li>
                        <li>Files cannot exceed 15 MB or 1,000,000 readings. Review accepted and rejected rows before confirming an upload.</li>
                        {selectedSensor?.supplier_name === 'Milesight' && selectedSensor?.quantity === 'Current' && selectedSensor?.channel_count === 3 &&
                            <li>This three-channel CT stores the supplied value as the average for all three channel readings.</li>}
                    </ul>

                    <Button variant="outline-secondary" onClick={downloadTemplate} disabled={loading} style={{marginBottom:'2vh'}}>
                        Download XLSX template
                    </Button>

                    <br/>

                    <input
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        disabled={loading}
                        onChange={event => {
                            setError('');
                            setFile(event.target.files[0] || null);
                        }}
                    />

                </>}

                {validation && !summary && <div>
                    <h5>Validation review</h5>
                    <p>{selectedSensor?.name} · {file?.name}</p>
                    <p>File readings: <strong>{validation.sourceRows.toLocaleString()}</strong><br/>
                        Accepted: <strong>{validation.totalRows.toLocaleString()}</strong><br/>
                        Rejected: <strong>{validation.rejectedRows.toLocaleString()}</strong></p>
                    {!resumeRef.current?.approved && <p>No readings have been uploaded. {validation.totalRows
                        ? 'Review the rejected rows, then confirm the accepted readings or choose another file.'
                        : 'There are no accepted readings to upload. Correct the file and try again.'}</p>}
                    {!resumeRef.current?.approved && <Button variant="outline-secondary" onClick={chooseAnotherFile} disabled={loading||reportBusy}>Choose another file</Button>}
                    {validation.rejectedRows>0 && <>
                        <p style={{marginTop:'1rem'}}>Rejected readings will be skipped only if you confirm below. For duplicate instants, the first valid row is accepted and later duplicates are listed here.</p>
                        <Button variant="outline-secondary" onClick={downloadRejections} disabled={loading||reportBusy}>Download rejected readings (CSV)</Button>
                        <div style={{overflow:'auto',maxHeight:'280px',marginTop:'1rem'}}>
                            <table className="table table-sm" aria-label="Rejected readings">
                                <thead><tr><th>Excel row</th><th>Timestamp</th><th>Value</th><th>Reason</th></tr></thead>
                                <tbody>{rejectedRows.map(row=><tr key={row.row}><td>{row.row}</td><td>{row.timestamp}</td><td>{row.value}</td><td>{row.reason}</td></tr>)}</tbody>
                            </table>
                        </div>
                        <div style={{display:'flex',gap:'1rem',alignItems:'center'}}>
                            <Button variant="outline-secondary" onClick={()=>changeRejectionPage(rejectedPage-1)} disabled={loading||reportBusy||rejectedPage===0}>Previous</Button>
                            <span>Page {rejectedPage+1} of {Math.ceil(validation.rejectedRows/50)}</span>
                            <Button variant="outline-secondary" onClick={()=>changeRejectionPage(rejectedPage+1)} disabled={loading||reportBusy||(rejectedPage+1)*50>=validation.rejectedRows}>Next</Button>
                        </div>
                    </>}
                </div>}
                {loading && <div style={{marginTop:'2vh'}}>
                    <LinearProgress variant="determinate" value={progress}/>
                    <p role="status" style={{marginTop:'0.5rem',marginBottom:0}}>{status}</p>
                </div>}

                {summary && <div>
                    <h5>Upload complete</h5>
                    <p style={{marginBottom:0}}>File readings: <strong>{summary.source_rows ?? summary.total}</strong></p>
                    <p style={{marginBottom:'0'}}>XLSX rows processed: <strong>{summary.total}</strong></p>
                    <p style={{marginBottom:'0'}}>Inserted timestamps: <strong>{summary.inserted}</strong></p>
                    <p style={{marginBottom:'0'}}>Overwritten readings: <strong>{summary.overwritten}</strong></p>
                    <p style={{marginBottom:'0'}}>Rejected rows: <strong>{summary.rejected}</strong></p>
                    <p style={{marginBottom:'0'}}>Affected cached intervals: <strong>{summary.affected_cached_buckets}</strong></p>
                    <p>Regenerated flattened minutes: <strong>{summary.flattened_minutes}</strong></p>
                    {validation?.rejectedRows>0 && <Button variant="outline-secondary" onClick={downloadRejections} disabled={reportBusy}>Download rejected readings (CSV)</Button>}
                </div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal} disabled={loading||reportBusy}>Close</Button>
                {!summary && !validation && <Button onClick={uploadReadings} disabled={loading||!timezone}>Validate file</Button>}
                {!summary && validation && <Button onClick={resumeRef.current?.approved?uploadReadings:confirmAcceptedReadings} disabled={loading||reportBusy||validation.totalRows===0}>
                    {resumeRef.current?.approved?'Retry / resume':validation.rejectedRows>0?'Upload accepted readings only':`Upload ${validation.totalRows.toLocaleString()} readings`}
                </Button>}
            </Modal.Footer>
        </Modal>
    );
};

export default ManualReadingsModal;
